import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import type { SxProps } from '@mui/material/styles';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { TargetingFields, type TargetingFieldsValues } from '@openx/types/targeting/targeting.ts';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TargetingSettingsProvider } from '../context/TargetingSettingsContext.tsx';
import { RequiredTargetingSectionKeys } from '../context/types.ts';
import { ChannelSection } from './ChannelSection.tsx';
import { ApplyRboAlert } from './components/ApplyRboAlert/ApplyRboAlert.tsx';
import { DisableRboAlert } from './components/DisableRboAlert/DisableRboAlert.tsx';
import { RboDisplayedAlertProvider } from './components/RboDisplayedAlertContext.tsx';
import { RboGroupValidationAlert } from './components/RboGroupValidationAlert/RboGroupValidationAlert.tsx';
import { RetargetingAquaValidationAlert } from './components/RetargetingAquaValidationAlert/RetargetingAquaValidationAlert.tsx';
import { DeviceSection } from './DeviceSection.tsx';
import { FormatSection } from './FormatSection.tsx';

const paperStyles: SxProps = {
  boxShadow: ELEVATION.elevation0,
  mb: 2,
  width: '100%',
  maxWidth: '40rem',
  py: 2,
  px: 3,
  borderColor: 'divider',
};

type RequiredTargetingSectionProps = {
  targetingParams?: RequiredTargetingFormType | null;
  readOnly?: boolean;
  onFieldUpdate?: (field: TargetingFieldsValues, newData: RequiredTargetingFormType, shouldValidate?: boolean) => void;
};

export const RequiredTargetingSection = ({
  onFieldUpdate,
  targetingParams,
  readOnly,
}: RequiredTargetingSectionProps) => {
  const { t } = useTranslation();
  const isViaInteraction = useRef<boolean>(false);

  const onTargetingUpdate = useCallback(
    (nextData: RequiredTargetingFormType) => {
      onFieldUpdate?.(TargetingFields.INVENTORY_FORMAT, nextData, isViaInteraction.current);
    },
    [onFieldUpdate]
  );

  const boxClickHandler = useCallback(() => {
    isViaInteraction.current = true;
  }, []);

  const formMethods = useFormContext();

  return (
    <Box sx={{ display: 'flex', width: '100%' }} onClickCapture={boxClickHandler}>
      <Paper data-test="targeting-section-required-presets" variant="outlined" sx={paperStyles}>
        <TargetingSettingsProvider
          initialState={{
            sections: {
              [RequiredTargetingSectionKeys.FORMAT]: {
                label: t('Format'),
                isLoading: true,
                elements: [],
              },
              [RequiredTargetingSectionKeys.CHANNEL]: {
                label: t('Channel'),
                isLoading: true,
                elements: [],
              },
              [RequiredTargetingSectionKeys.DEVICE]: {
                label: t('Device'),
                isLoading: true,
                elements: [],
              },
            },
          }}
          onTargetingUpdate={onTargetingUpdate}
        >
          <FormatSection readOnly={readOnly} targetingParams={targetingParams} />
          <ChannelSection readOnly={readOnly} targetingParams={targetingParams} />
          <DeviceSection readOnly={readOnly} targetingParams={targetingParams} />
        </TargetingSettingsProvider>

        {formMethods && (
          <RboDisplayedAlertProvider>
            <RboGroupValidationAlert />
            <ApplyRboAlert targetingParams={targetingParams} />
            <DisableRboAlert targetingParams={targetingParams} />
            <RetargetingAquaValidationAlert />
          </RboDisplayedAlertProvider>
        )}
      </Paper>
    </Box>
  );
};
